/**
 * `/my-data` — the employee's own copy of their own data (Tier-3 U8).
 *
 * The shape file is `web/shapes/my-data.md` and it was committed first. Two things
 * decided there rather than here:
 *
 *  - **Region order is a dependency, not a preference.** Tier-3 U9's access-log
 *    region ("ai đã xem dữ liệu của tôi") sits BELOW the export region, on this
 *    screen and on the phone. Both are a single `<section>` with `data-region` on
 *    it rather than loose markup this screen's `<div>` happens to contain, and
 *    `my-data.test.tsx` asserts the order in both directions — export first,
 *    access-log second — so neither can drift above the other and stay green.
 *  - **Three lifetimes, never one sentence.** The link lives minutes, the file lives
 *    seven days, the record is kept. "Expires in three minutes" about a file, or
 *    "valid for a week" about a link, is wrong in the direction that loses somebody
 *    their copy — so the numbers come from the server's own `policy` block and are
 *    printed separately.
 *
 * ── The poll stops ─────────────────────────────────────────────────────────
 *
 * `usePolledQuery` with an interval of `0` runs once and sets no timer. So the
 * screen polls only while a request is actually `pending` or `running`, and an
 * employee who leaves this tab open on a finished export costs the tenant nothing.
 *
 * ── Nothing here holds a URL ───────────────────────────────────────────────
 *
 * `requestDsarDownload` spends the credential through `navigate` and hands back
 * facts. There is no `url` in this component's state, so a stale link cannot be
 * rendered — see `api/dsar.ts`'s own header for why that is structural rather than
 * a habit.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { formatInstant } from '../i18n/datetime';
import { useSession } from '../auth/SessionProvider';
import { usePolledQuery } from '../api/usePolledQuery';
import { useApiMutation } from '../api/useApiMutation';
import {
  DSAR_MY_REQUESTS_PATH,
  isInFlight,
  requestDsarDownload,
  type DsarDownloadState,
  type DsarRequestRecord,
  type DsarRequestListResponse,
  type DsarRequestResponse,
  type DsarStatus,
  type Navigate,
} from '../api/dsar';
import { accessLogPath, type AccessLogEntry, type AccessLogPage } from '../api/access-log';
import { WorkforceApiError } from '../api/errors';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import type { IconName, Severity } from '../states/icons';
import { ScreenHeading } from '../shell/ScreenHeading';

/** How often the list re-reads while something is being assembled. */
const POLL_MS = 5_000;

export interface MyDataScreenProps {
  /** Injected so a test drives the download navigation without an address bar. */
  readonly navigate?: Navigate;
}

/**
 * The badge each status renders as.
 *
 * Icon **and** text, and the icon is the status's own rather than the severity's —
 * `DESIGN.md` §4, and the correction Task T2-B had to make: `SEVERITY_ICON` would
 * draw the same glyph for `pending` and `running`, which are the two states a person
 * on this screen most needs to tell apart. Colour is the third channel, never the
 * first.
 */
const STATUS_BADGE: Readonly<
  Record<DsarStatus, { severity: Severity; icon: IconName; label: MessageKey }>
> = {
  pending: { severity: 'info', icon: 'hourglass', label: 'myData.status.pending' },
  running: { severity: 'info', icon: 'clock', label: 'myData.status.running' },
  done: { severity: 'low', icon: 'circle-check', label: 'myData.status.done' },
  failed: { severity: 'medium', icon: 'circle-alert', label: 'myData.status.failed' },
};

export function MyDataScreen({ navigate }: MyDataScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { client, user } = useSession();
  const timezone = user?.timezone ?? 'Asia/Ho_Chi_Minh';

  const [reloadToken, setReloadToken] = useState(0);
  const [download, setDownload] = useState<DsarDownloadState>({ status: 'idle' });
  const [polling, setPolling] = useState(false);

  const list = usePolledQuery<DsarRequestListResponse>(
    DSAR_MY_REQUESTS_PATH,
    polling ? POLL_MS : 0,
    reloadToken,
  );

  const rows = list.data?.data ?? [];
  const policy = list.data?.policy ?? null;
  const anyInFlight = rows.some(isInFlight);
  // Derived from what the last read SAID, not from what the last press hoped. A
  // screen that started polling on submit and never checked would keep a timer for
  // ever if the create was refused — and one that never stopped would poll a
  // finished export until the tab closed.
  useEffect(() => {
    setPolling(anyInFlight);
  }, [anyInFlight]);

  const create = useApiMutation<Record<string, never>, DsarRequestResponse>(
    DSAR_MY_REQUESTS_PATH,
    'POST',
  );

  const reload = useCallback(() => {
    setReloadToken((value) => value + 1);
  }, []);

  const onRequest = useCallback(() => {
    void create.submit({}).then(reload);
  }, [create, reload]);

  const onDownload = useCallback(
    (requestId: string) => {
      setDownload({ status: 'requesting', requestId });
      void requestDsarDownload(
        client,
        requestId,
        navigate ??
          ((url: string) => {
            window.location.assign(url);
          }),
      ).then((outcome) => {
        setDownload(outcome.state);
        // The row's state was stale — re-read rather than guess which way it moved.
        if (outcome.staleStatus) reload();
      });
    },
    [client, navigate, reload],
  );

  const newest = rows[0] ?? null;
  const earlier = rows.slice(1);

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.myData')}</ScreenHeading>
      <p className="wp-small wp-muted" data-testid="my-data-intro">
        {t('myData.intro')}
      </p>

      {/* ── REGION 1 (U8): the export request and its state. U9's access-log
          region goes BELOW this one — see the shape file. ─────────────────── */}
      {/* `data-region` is what makes the order above ASSERTABLE rather than merely
          documented: `my-data.test.tsx` reads every `[data-region]` in the container
          and requires this one to be index 0, so U9's `data-region="access-log"`
          cannot land above it and stay green. */}
      <section
        className="wp-section"
        aria-labelledby="my-data-export-heading"
        data-region="export"
        data-testid="my-data-export"
      >
        <h2 className="wp-section__title" id="my-data-export-heading" tabIndex={-1}>
          {t('myData.export.title')}
        </h2>

        {policy === null ? null : (
          <p className="wp-small wp-muted" data-testid="my-data-policy">
            {t('myData.export.policy', {
              cap: policy.daily_request_cap,
              hours: policy.completion_target_hours,
              days: policy.file_available_days,
            })}
          </p>
        )}

        {/* Absent, not greyed, while one is open: a button that can only produce a
            409 is not a button, and the state above it already says why. */}
        {anyInFlight ? null : (
          <div className="wp-note__actions">
            <button
              type="button"
              className="wp-button wp-button--primary"
              onClick={onRequest}
              disabled={create.status === 'pending'}
              data-testid="my-data-request"
            >
              {create.status === 'pending' ? t('myData.export.requesting') : t('myData.export.cta')}
            </button>
          </div>
        )}

        {create.error === null ? null : (
          <ApiErrorNote
            error={create.error}
            subject={t('myData.export.subject')}
            testId="my-data-create-error"
          />
        )}

        {list.status === 'loading' ? (
          <Skeleton rows={2} label={t('myData.export.loading')} testId="my-data-skeleton" />
        ) : null}

        {list.status === 'error' && list.error !== null && list.data === null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={reload}
            subject={t('myData.export.subject')}
            testId="my-data-list-error"
          />
        ) : null}

        {list.status !== 'loading' && rows.length === 0 ? (
          <EmptyState
            title={t('myData.export.empty.title')}
            description={t('myData.export.empty.body')}
            testId="my-data-empty"
          />
        ) : null}

        {newest === null ? null : (
          <RequestCard
            request={newest}
            locale={locale}
            timezone={timezone}
            download={download}
            onDownload={onDownload}
            testId="my-data-newest"
          />
        )}

        {earlier.length === 0 ? null : (
          <div className="wp-rows" data-testid="my-data-history">
            <p className="wp-small wp-muted">{t('myData.export.history')}</p>
            {earlier.map((request) => (
              <RequestCard
                key={request.id}
                request={request}
                locale={locale}
                timezone={timezone}
                download={download}
                onDownload={onDownload}
                testId={`my-data-row-${request.id}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── REGION 2 (U9): who has looked at my data. BELOW the export region,
          per `web/shapes/my-data.md` §2.1 — and carrying the `data-region`
          marker that section requires, because a region without one renders
          wherever it is put and no test notices. ─────────────────────────── */}
      <AccessLogRegion locale={locale} timezone={timezone} />
    </div>
  );
}

interface RequestCardProps {
  readonly request: DsarRequestRecord;
  readonly locale: string;
  readonly timezone: string;
  readonly download: DsarDownloadState;
  readonly onDownload: (requestId: string) => void;
  readonly testId: string;
}

function RequestCard({
  request,
  locale,
  timezone,
  download,
  onDownload,
  testId,
}: RequestCardProps): ReactNode {
  const { t } = useI18n();
  const refused =
    download.status === 'refused' && download.requestId === request.id ? download.error : null;

  return (
    <div className="wp-card" data-testid={testId}>
      {/* `role="status"` so the change is announced without stealing focus from a
          reader who has moved on — `web/shapes/my-data.md` §7. */}
      <p className="wp-row" role="status" data-testid={`${testId}-status`}>
        <Badge
          severity={STATUS_BADGE[request.status].severity}
          icon={STATUS_BADGE[request.status].icon}
          label={t(STATUS_BADGE[request.status].label)}
        />
        <span className="wp-small wp-muted">
          {t('myData.requestedAt', { at: formatInstant(request.requested_at, locale, timezone) })}
        </span>
      </p>

      {request.status === 'done' && request.expires_at !== null ? (
        <p className="wp-small wp-muted" data-testid={`${testId}-expiry`}>
          {request.download_available
            ? t('myData.fileUntil', {
                at: formatInstant(request.expires_at, locale, timezone),
              })
            : t('myData.fileGone')}
        </p>
      ) : null}

      {/* Absent rather than disabled for every state that is not a downloadable
          file: "it is not made yet" and "you may not have this" are different
          sentences, and a greyed control says the second one. */}
      {request.download_available ? (
        <div className="wp-note__actions">
          <button
            type="button"
            className="wp-button"
            onClick={() => {
              onDownload(request.id);
            }}
            disabled={download.status === 'requesting' && download.requestId === request.id}
            data-testid={`${testId}-download`}
          >
            {t('myData.download')}
          </button>
        </div>
      ) : null}

      {request.status === 'failed' ? (
        <StatusNote severity="medium" title={t('myData.failed.title')} testId={`${testId}-failed`}>
          {t('myData.failed.body')}
        </StatusNote>
      ) : null}

      {refused === null ? null : (
        <ApiErrorNote
          error={refused}
          subject={t('myData.export.subject')}
          testId={`${testId}-download-error`}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════ REGION 2 — who has looked at my data (U9) */

/**
 * The verbs and the kinds, translated.
 *
 * `access_type` and `resource_type` are database enums a migration may extend, and
 * `openapi.yaml` deliberately publishes them as strings rather than closed enums for
 * that reason. So the lookup **falls back to the raw token** instead of throwing or
 * rendering an empty cell: an employee seeing `usb_event` has been told something
 * true and slightly ugly, which is the right failure when the alternative is a blank
 * where the answer should be. `translate()` cannot be handed an unknown key — it
 * would interpolate `undefined` — so the fallback is a real branch, not a default
 * argument.
 */
const ACCESS_VERB: Readonly<Record<string, MessageKey>> = {
  view: 'myData.access.verb.view',
  list: 'myData.access.verb.list',
  export: 'myData.access.verb.export',
  download: 'myData.access.verb.download',
  ai_query: 'myData.access.verb.ai_query',
};

const ACCESS_KIND: Readonly<Record<string, MessageKey>> = {
  screenshot: 'myData.access.kind.screenshot',
  usb_event: 'myData.access.kind.usb_event',
  file_event: 'myData.access.kind.file_event',
  audit_log: 'myData.access.kind.audit_log',
  employee_activity: 'myData.access.kind.employee_activity',
  employee_attendance: 'myData.access.kind.employee_attendance',
  report: 'myData.access.kind.report',
  incident_evidence: 'myData.access.kind.incident_evidence',
  ai_insight: 'myData.access.kind.ai_insight',
  candidate_document: 'myData.access.kind.candidate_document',
  employee_document: 'myData.access.kind.employee_document',
  employee_location: 'myData.access.kind.employee_location',
  dsar_export: 'myData.access.kind.dsar_export',
};

interface AccessLogRegionProps {
  readonly locale: string;
  readonly timezone: string;
}

type AccessLogStatus = 'loading' | 'ok' | 'error';

/**
 * `GET /api/v1/audit/my-access-log`, ten rows at a time.
 *
 * ── Why this region keeps its own state instead of using `useApiQuery` ─────
 *
 * It **appends**. A cursor walk is the one read shape on this build where the
 * previous answer is part of the next one: `usePolledQuery` and `useApiQuery` both
 * replace `data` on every fetch, which is right for a list that re-reads itself and
 * wrong for one that grows. Pressing "show more" through either would render page
 * two *instead of* page one.
 *
 * ── It does not poll ───────────────────────────────────────────────────────
 *
 * The export region above polls because it is waiting for a job it started. Nothing
 * here is in flight — a new row appears when somebody else reads your data, which is
 * not an event this screen is waiting on — so a timer would be a request per interval
 * per employee for an answer that almost never changes.
 */
function AccessLogRegion({ locale, timezone }: AccessLogRegionProps): ReactNode {
  const { t } = useI18n();
  const { client } = useSession();

  const [status, setStatus] = useState<AccessLogStatus>('loading');
  const [rows, setRows] = useState<readonly AccessLogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<WorkforceApiError | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    client
      .request<AccessLogPage>(accessLogPath())
      .then((page) => {
        if (cancelled) return;
        setRows(page.data);
        setCursor(page.page.next_cursor);
        setHasMore(page.page.has_more);
        setError(null);
        setStatus('ok');
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause instanceof WorkforceApiError ? cause : null);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [client, reloadToken]);

  const reload = useCallback(() => {
    // A retry starts the walk again rather than resuming it: the cursor in hand
    // belongs to a page that may never have arrived, and resuming from it would
    // silently drop everything before it.
    setRows([]);
    setCursor(null);
    setHasMore(false);
    setReloadToken((value) => value + 1);
  }, []);

  const loadMore = useCallback(() => {
    if (cursor === null) return;
    setLoadingMore(true);
    client
      .request<AccessLogPage>(accessLogPath(cursor))
      .then((page) => {
        setRows((current) => [...current, ...page.data]);
        setCursor(page.page.next_cursor);
        setHasMore(page.page.has_more);
        setLoadingMore(false);
      })
      .catch((cause: unknown) => {
        setLoadingMore(false);
        setError(cause instanceof WorkforceApiError ? cause : null);
      });
  }, [client, cursor]);

  return (
    <section
      className="wp-section"
      aria-labelledby="my-data-access-heading"
      data-region="access-log"
      data-testid="my-data-access"
    >
      <h2 className="wp-section__title" id="my-data-access-heading" tabIndex={-1}>
        {t('myData.access.title')}
      </h2>
      <p className="wp-small wp-muted" data-testid="my-data-access-intro">
        {t('myData.access.intro')}
      </p>

      {status === 'loading' ? (
        <Skeleton rows={3} label={t('myData.access.loading')} testId="my-data-access-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('myData.access.subject')}
          testId="my-data-access-error"
        />
      ) : null}

      {status === 'ok' && rows.length === 0 ? (
        <EmptyState
          title={t('myData.access.empty.title')}
          description={t('myData.access.empty.body')}
          testId="my-data-access-empty"
        />
      ) : null}

      {rows.length === 0 ? null : (
        // A ruled table, not a stack of cards. This is a log the person reads down —
        // "who, what, when, why" is the same four questions on every line, and four
        // cards' worth of chrome per answer is what stopped it being readable at 390px.
        <div className="wp-table-scroll" data-testid="my-data-access-rows">
          <table className="wp-table wp-accesslog">
            <caption className="wp-visually-hidden">{t('myData.access.title')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('myData.access.col.actor')}</th>
                <th scope="col">{t('myData.access.col.what')}</th>
                <th scope="col">{t('myData.access.col.at')}</th>
                <th scope="col">{t('myData.access.col.reason')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <AccessLogRow key={entry.id} entry={entry} locale={locale} timezone={timezone} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Absent rather than disabled when there is no next page: "there is no more"
          and "you may not have more" are different sentences, and a greyed control
          says the second one. */}
      {hasMore ? (
        <div className="wp-note__actions">
          <button
            type="button"
            className="wp-button"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="my-data-access-more"
          >
            {loadingMore ? t('myData.access.moreLoading') : t('myData.access.more')}
          </button>
        </div>
      ) : null}
    </section>
  );
}

interface AccessLogRowProps {
  readonly entry: AccessLogEntry;
  readonly locale: string;
  readonly timezone: string;
}

/**
 * One read, rendered.
 *
 * The `company` badge is **text plus an icon**, never colour alone (`DESIGN.md` §4),
 * and it is on the row rather than in a separate list: a company-wide read is not a
 * lesser kind of row to be tucked away, it is a read of data that includes the
 * person's own, and the label says only that it was not aimed at them personally.
 */
function AccessLogRow({ entry, locale, timezone }: AccessLogRowProps): ReactNode {
  const { t } = useI18n();
  const verbKey = ACCESS_VERB[entry.access_type];
  const kindKey = ACCESS_KIND[entry.resource_type];

  return (
    <tr data-testid={`my-data-access-row-${entry.id}`}>
      <th scope="row">
        <span data-testid={`my-data-access-row-${entry.id}-actor`}>{entry.actor_label}</span>
        {entry.scope === 'company' ? (
          <>
            {' '}
            <Badge
              severity="info"
              icon="info"
              label={t('myData.access.companyWide')}
              testId={`my-data-access-row-${entry.id}-company`}
            />
          </>
        ) : null}
      </th>
      <td data-testid={`my-data-access-row-${entry.id}-what`}>
        {t('myData.access.what', {
          verb: verbKey === undefined ? entry.access_type : t(verbKey),
          kind: kindKey === undefined ? entry.resource_type : t(kindKey),
        })}
      </td>
      <td className="wp-accesslog__at" data-testid={`my-data-access-row-${entry.id}-at`}>
        {t('myData.access.at', { at: formatInstant(entry.occurred_at, locale, timezone) })}
      </td>
      {/* Empty when the column is null rather than printed as an empty reason:
          `wp_data_access_reason_ck` requires one only for a sensitive read, and a
          blank "Lý do:" would claim a reason was given and was empty. */}
      {entry.reason === null ? (
        <td />
      ) : (
        <td className="wp-small wp-muted" data-testid={`my-data-access-row-${entry.id}-reason`}>
          {t('myData.access.reason', { reason: entry.reason })}
        </td>
      )}
    </tr>
  );
}
